import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { GroupMenuDto, GroupMenuOption, GroupMenuPayload } from '../../../models/system-settings/group-menus.model';
import { GroupSubMenuDto, GroupSubMenuOption, GroupSubMenuPayload } from '../../../models/system-settings/group-sub-menus.model';
import { MenuListDto, MenuListOption, MenuListPayload } from '../../../models/system-settings/menu-lists.model';
import {
  AccessUserDetailDto,
  AccessUserListDto,
  PagedTableResponse,
  AccessUserCpRolePayload,
  BackofficeUserDetailDto,
  BackofficeUserLoginLogDto,
  BackofficeUserSessionDto,
  BackofficeUserPayload,
  ResetBackofficeUserPasswordPayload,
  KickBackofficeUserSessionPayload,
} from '../../../models/system-settings/access-users.model';
import { AccessRoleDto, RolePayload } from '../../../models/system-settings/manage-roles.model';
import { AccessMenuRoleDto, MenuRolePayload } from '../../../models/system-settings/manage-roles-menu.model';
import {
  LiffHomeMenuConfigResponseDto,
  LiffHomeMenuConfigSavePayload,
} from '../../../models/system-settings/liff-home-menu.model';
import {
  LineGuideConfigResponse,
  LineGuideConfigSavePayload,
} from '../../../models/system-settings/line-guide-menu.model';
import {
  PublicGuideSavePayload,
  PublicGuideTableResponse,
} from '../../../models/system-settings/public-guide.model';
import {
  SitePublicSavePayload,
  SitePublicTableResponse,
} from '../../../models/system-settings/site-public-profile.model';
import {
  LineBroadcastActionPayload,
  LineBroadcastActionResponse,
  LineBroadcastAudienceSummaryDto,
  LineBroadcastDetailDto,
  LineBroadcastImageUploadResponse,
  LineBroadcastAudienceMembersResponse,
  LineBroadcastLinkSettingsDto,
  LineBroadcastMemberCardsResponse,
  LineBroadcastNewsOptionDto,
  LineBroadcastProgressDto,
  LineBroadcastQuotaDto,
  LineBroadcastSavePayload,
  LineBroadcastSiteOptionDto,
  LineBroadcastTableResponse,
  LineBroadcastTestSendPayload,
} from '../../../models/system-settings/line-broadcast.model';
import {
  AllocateClientFreeQuotaPayload,
  ClientFreeQuotaDto,
  ClientFreeQuotaTableResponse,
  FreeParkingSubscriptionOptionDto,
  FreeQuotaClientOptionDto,
  SaveSiteFreeQuotaPayload,
  SaveUserSiteAccessPayload,
  SiteFreeQuotaDto,
  UpdateClientFreeQuotaPayload,
  UserSiteAccessDto,
} from '../../../models/system-settings/free-quota.model';
import {
  FreeParkingDashboard,
  FreeParkingCardListItem,
  SaveAccessGroupPayload,
  ValidateFreeParkingAdvancePayload,
  AccessGroupListItem,
  AccessGroupScope,
} from '../../../models/system-settings/access-group.model';

@Injectable({
  providedIn: 'root',
})
export class ApiSystemSettingsService {
  private baseUrl = environment.apiPrivateUrl;
  private cookieService = inject(CookieService);
  private apiMethodService = inject(ApiMethodService);

  // ================= Normal Functions =================

  constructor() { }

  // ================= API Functions =================

  // ─── Group Menus API ───

  /**
   * Fetches the group menus table data from the API.
   */
  getGroupMenusForTable(): Observable<GroupMenuDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<GroupMenuDto[]>(
      '/SystemSettings/getGroupMenusForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getGroupMenusForTable',
        token: token,
        headerOnly: false,
      }
    );
  }

  /**
   * Fetches active group menu options for dropdown selectors.
   */
  getGroupMenuOptions(): Observable<GroupMenuOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<GroupMenuOption[]>(
      '/SystemSettings/getGroupMenuOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getGroupMenuOptions',
        token: token,
        headerOnly: false,
      }
    );
  }

  /**
   * Creates a new Group Menu record.
   */
  createGroupMenu(payload: GroupMenuPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertGroupMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'InsertGroupMenu',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Updates an existing Group Menu record.
   */
  updateGroupMenu(payload: GroupMenuPayload, id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateGroupMenu/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateGroupMenu',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Deletes a Group Menu record by its ID.
   */
  deleteGroupMenu(id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/deleteGroupMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteGroupMenu',
        token: token,
        headerOnly: false,
        params: { id: id },
      }
    );
  }

  // ─── Group Sub Menus API ───

  /**
   * Fetches the group sub menus table data from the API, optionally filtered by parent group.
   */
  getGroupSubMenusForTable(groupMenuId?: number): Observable<GroupSubMenuDto[]> {
    const token = this.cookieService.get('token');
    const params: any = {};
    if (groupMenuId !== undefined && groupMenuId !== null) {
      params.groupMenuId = groupMenuId;
    }
    return this.apiMethodService.request<GroupSubMenuDto[]>(
      '/SystemSettings/getGroupSubMenusForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getGroupSubMenusForTable',
        token: token,
        headerOnly: false,
        params: params,
      }
    );
  }

  /**
   * Fetches active group sub menu options for dropdown selectors, optionally filtered by parent group.
   */
  getGroupSubMenuOptions(groupMenuId?: number): Observable<GroupSubMenuOption[]> {
    const token = this.cookieService.get('token');
    const params: any = {};
    if (groupMenuId !== undefined && groupMenuId !== null) {
      params.groupMenuId = groupMenuId;
    }
    return this.apiMethodService.request<GroupSubMenuOption[]>(
      '/SystemSettings/getGroupSubMenuOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getGroupSubMenuOptions',
        token: token,
        headerOnly: false,
        params: params,
      }
    );
  }

  /**
   * Creates a new Group Sub Menu record.
   */
  createGroupSubMenu(payload: GroupSubMenuPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertGroupSubMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertGroupSubMenu',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Updates an existing Group Sub Menu record.
   */
  updateGroupSubMenu(payload: GroupSubMenuPayload, id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateGroupSubMenu/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateGroupSubMenu',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Deletes a Group Sub Menu record by its ID.
   */
  deleteGroupSubMenu(id: number, userId: number, menuId: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/deleteGroupSubMenu/${id}`,
      'DELETE',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteGroupSubMenu',
        token: token,
        headerOnly: false,
        params: { userId, menuId },
      }
    );
  }

  // ─── Menu Lists API ───

  /**
   * Fetches the menu lists table data from the API, optionally filtered by parent group and submenu group.
   */
  getMenuListsForTable(groupMenuId?: number, groupSubId?: number): Observable<MenuListDto[]> {
    const token = this.cookieService.get('token');
    const params: any = {};
    if (groupMenuId !== undefined && groupMenuId !== null) {
      params.groupMenuId = groupMenuId;
    }
    if (groupSubId !== undefined && groupSubId !== null) {
      params.groupSubId = groupSubId;
    }
    return this.apiMethodService.request<MenuListDto[]>(
      '/SystemSettings/getMenuListsForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getMenuListsForTable',
        token: token,
        headerOnly: false,
        params: params,
      }
    );
  }

  /**
   * Fetches active menu list options, optionally filtered.
   */
  getMenuListOptions(
    groupMenuId?: number,
    groupSubId?: number,
    roleId?: number,
    includeMenuListId?: number
  ): Observable<MenuListOption[]> {
    const token = this.cookieService.get('token');
    const params: any = {};
    if (groupMenuId !== undefined && groupMenuId !== null) {
      params.groupMenuId = groupMenuId;
    }
    if (groupSubId !== undefined && groupSubId !== null) {
      params.groupSubId = groupSubId;
    }
    if (roleId !== undefined && roleId !== null && roleId > 0) {
      params.roleId = roleId;
    }
    if (includeMenuListId !== undefined && includeMenuListId !== null && includeMenuListId > 0) {
      params.includeMenuListId = includeMenuListId;
    }
    return this.apiMethodService.request<MenuListOption[]>(
      '/SystemSettings/getMenuListOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getMenuListOptions',
        token: token,
        headerOnly: false,
        params: params,
      }
    );
  }

  /**
   * Creates a new Menu List record.
   */
  createMenuList(payload: MenuListPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertMenuList',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertMenuList',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Updates an existing Menu List record.
   */
  updateMenuList(payload: MenuListPayload, id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateMenuList/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateMenuList',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Deletes a Menu List record by its ID.
   */
  deleteMenuList(id: number, userId: number, menuId: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/deleteMenuList/${id}`,
      'DELETE',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteMenuList',
        token: token,
        headerOnly: false,
        params: { userId, menuId },
      }
    );
  }

  // ─── Manage Roles API ───

  getAccessRolesForTable(): Observable<AccessRoleDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AccessRoleDto[]>(
      '/SystemSettings/getAccessRolesForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessRolesForTable',
        token: token,
        headerOnly: false,
      }
    );
  }

  createRole(payload: RolePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertRole',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertRole',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  updateRole(payload: RolePayload, id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateRole/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateRole',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  // ─── Manage Role Menus API ───

  getAccessMenuRolesForTable(roleId?: number): Observable<AccessMenuRoleDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, number> = {};
    if (roleId !== undefined && roleId !== null) {
      params['roleId'] = roleId;
    }
    return this.apiMethodService.request<AccessMenuRoleDto[]>(
      '/SystemSettings/getAccessMenuRolesForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessMenuRolesForTable',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  createMenuRole(payload: MenuRolePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertMenuRole',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertMenuRole',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  updateMenuRole(payload: MenuRolePayload, id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateMenuRole/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateMenuRole',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deleteMenuRole(id: number, userId: number, menuId: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/deleteMenuRole/${id}`,
      'DELETE',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteMenuRole',
        token: token,
        headerOnly: false,
        params: { userId, menuId },
      }
    );
  }

  // ─── Access User API ───

  getAccessUsersForTable(
    page = 1,
    pageSize = 20,
    searchField?: string,
    searchText?: string
  ): Observable<PagedTableResponse<AccessUserListDto>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { page, pageSize };
    if (searchField) params['searchField'] = searchField;
    if (searchText) params['searchText'] = searchText;

    return this.apiMethodService.request<PagedTableResponse<AccessUserListDto>>(
      '/SystemSettings/getAccessUsersForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessUsersForTable',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getDepartmentsForTable(
    page = 1,
    pageSize = 20,
    siteId?: number | null,
    searchText?: string | null
  ): Observable<PagedTableResponse<{ id: number; name: string; siteId?: number | null; siteName?: string }>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { page, pageSize };
    if (siteId) params['siteId'] = siteId;
    if (searchText?.trim()) params['searchText'] = searchText.trim();

    return this.apiMethodService.request(
      '/SystemSettings/getDepartmentsForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getDepartmentsForTable',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  updateAccessUserCpRole(id: number, payload: AccessUserCpRolePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateAccessUserCpRole/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateAccessUserCpRole',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getParkingSiteOptions(): Observable<{ id: number; name: string }[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<{ id: number; name: string }[]>(
      '/SystemSettings/getParkingSiteOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingSiteOptions',
        token: token,
        headerOnly: false,
      }
    );
  }

  /**
   * Fetches access-user detail (including accessible sites) by user id.
   */
  getAccessUserById(userId: number): Observable<AccessUserDetailDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AccessUserDetailDto>(
      '/SystemSettings/getAccessUserById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessUserById',
        token: token,
        headerOnly: false,
        params: { userId },
      }
    );
  }

  getBackofficeUserById(userId: number): Observable<BackofficeUserDetailDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<BackofficeUserDetailDto>(
      '/SystemSettings/getBackofficeUserById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getBackofficeUserById',
        token: token,
        headerOnly: false,
        params: { userId },
      }
    );
  }

  getBackofficeUserLoginLogsForTable(
    userId: number,
    page = 1,
    pageSize = 20,
    eventType?: string,
  ): Observable<PagedTableResponse<BackofficeUserLoginLogDto>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { userId, page, pageSize };
    if (eventType) params['eventType'] = eventType;

    return this.apiMethodService.request<PagedTableResponse<BackofficeUserLoginLogDto>>(
      '/SystemSettings/getBackofficeUserLoginLogsForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getBackofficeUserLoginLogsForTable',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  insertBackofficeUser(payload: BackofficeUserPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/insertBackofficeUser',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertBackofficeUser',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  updateBackofficeUser(id: number, payload: BackofficeUserPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/updateBackofficeUser/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateBackofficeUser',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  resetBackofficeUserPassword(id: number, payload: ResetBackofficeUserPasswordPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/resetBackofficeUserPassword/${id}`,
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'resetBackofficeUserPassword',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /**
   * Fetches full detail of a user by ID, including accessible sites list.
   * Used on login to populate the global site selector.
   * @param userId The logged-in user's ID from cookie
   */
  /**
   * Imports menu hierarchy (Group → Sub → Menu List) in one API call.
   * Does not touch role permissions. Match by name / pathId.
   */
  getLiffHomeMenuConfig(): Observable<LiffHomeMenuConfigResponseDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LiffHomeMenuConfigResponseDto>(
      '/SystemSettings/getLiffHomeMenuConfig',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLiffHomeMenuConfig',
        token: token,
        headerOnly: false,
      }
    );
  }

  saveLiffHomeMenuConfig(payload: LiffHomeMenuConfigSavePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/saveLiffHomeMenuConfig',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveLiffHomeMenuConfig',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  importLiffHomeMenuConfig(payload: LiffHomeMenuConfigSavePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/importLiffHomeMenuConfig',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'importLiffHomeMenuConfig',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getSiteFreeQuotas(siteId: number): Observable<SiteFreeQuotaDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<SiteFreeQuotaDto[]>(
      '/SystemSettings/getSiteFreeQuotas',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getSiteFreeQuotas',
        token: token,
        headerOnly: false,
        params: { siteId },
      }
    );
  }

  getClientFreeQuotas(siteId: number, clientId?: number | null): Observable<ClientFreeQuotaDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, number> = { siteId };
    if (clientId != null && clientId > 0) {
      params['clientId'] = clientId;
    }
    return this.apiMethodService.request<ClientFreeQuotaDto[]>(
      '/SystemSettings/getClientFreeQuotas',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getClientFreeQuotas',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getUserSiteAccess(
    resUserId?: number | null,
    siteId?: number | null
  ): Observable<UserSiteAccessDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, number> = {};
    if (resUserId != null && resUserId > 0) {
      params['resUserId'] = resUserId;
    }
    if (siteId != null && siteId > 0) {
      params['siteId'] = siteId;
    }
    return this.apiMethodService.request<UserSiteAccessDto[]>(
      '/SystemSettings/getUserSiteAccess',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getUserSiteAccess',
        token: token,
        headerOnly: false,
        params: Object.keys(params).length ? params : undefined,
      }
    );
  }

  getFreeParkingSubscriptions(
    siteId: number,
    vehicleTypeId?: number | null
  ): Observable<FreeParkingSubscriptionOptionDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, number> = { siteId };
    if (vehicleTypeId != null && vehicleTypeId > 0) {
      params['vehicleTypeId'] = vehicleTypeId;
    }
    return this.apiMethodService.request<FreeParkingSubscriptionOptionDto[]>(
      '/SystemSettings/getFreeParkingSubscriptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFreeParkingSubscriptions',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  saveSiteFreeQuota(payload: SaveSiteFreeQuotaPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/saveSiteFreeQuota',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveSiteFreeQuota',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  allocateClientFreeQuota(payload: AllocateClientFreeQuotaPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/allocateClientFreeQuota',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'allocateClientFreeQuota',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  updateClientFreeQuota(payload: UpdateClientFreeQuotaPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/updateClientFreeQuota',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateClientFreeQuota',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  saveUserSiteAccess(payload: SaveUserSiteAccessPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/saveUserSiteAccess',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveUserSiteAccess',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getAccessGroups(siteId?: number | null): Observable<AccessGroupListItem[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, number> = {};
    if (siteId != null && siteId > 0) params['siteId'] = siteId;
    return this.apiMethodService.request<AccessGroupListItem[]>(
      '/SystemSettings/getAccessGroups',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessGroups',
        token,
        headerOnly: false,
        params: Object.keys(params).length ? params : undefined,
      }
    );
  }

  getMyAccessGroupScope(resUserId: number, siteId: number): Observable<AccessGroupScope | null> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AccessGroupScope | null>(
      '/SystemSettings/getMyAccessGroupScope',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getMyAccessGroupScope',
        token,
        headerOnly: false,
        params: { resUserId, siteId },
      }
    );
  }

  getFreeParkingDashboard(resUserId: number, siteId: number): Observable<FreeParkingDashboard> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<FreeParkingDashboard>(
      '/SystemSettings/getFreeParkingDashboard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFreeParkingDashboard',
        token,
        headerOnly: false,
        params: { resUserId, siteId },
      }
    );
  }

  getFreeParkingCards(
    resUserId: number,
    siteId: number,
    search?: string | null
  ): Observable<FreeParkingCardListItem[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { resUserId, siteId };
    if (search) params['search'] = search;
    return this.apiMethodService.request<FreeParkingCardListItem[]>(
      '/SystemSettings/getFreeParkingCards',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFreeParkingCards',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  saveAccessGroup(payload: SaveAccessGroupPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/saveAccessGroup',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveAccessGroup',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  validateFreeParkingAdvance(payload: ValidateFreeParkingAdvancePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/validateFreeParkingAdvance',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'validateFreeParkingAdvance',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  importMenuStructure(payload: {
    mode: 'skip' | 'update' | 'force';
    userId: number;
    menuId: number;
    hierarchy: any[];
  }): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/importMenuStructure',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'importMenuStructure',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getBackofficeUserSessions(userId: number): Observable<BackofficeUserSessionDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<BackofficeUserSessionDto[]>(
      '/SystemSettings/getBackofficeUserSessions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getBackofficeUserSessions',
        token: token,
        headerOnly: false,
        params: { userId },
      }
    );
  }

  kickBackofficeUserSession(sessionId: number, payload: KickBackofficeUserSessionPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/kickBackofficeUserSession/${sessionId}`,
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'kickBackofficeUserSession',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  kickAllBackofficeUserSessions(userId: number, payload: KickBackofficeUserSessionPayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      `/SystemSettings/kickAllBackofficeUserSessions/${userId}`,
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'kickAllBackofficeUserSessions',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getLineGuideConfig(): Observable<LineGuideConfigResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineGuideConfigResponse>(
      '/SystemSettings/getLineGuideConfig',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineGuideConfig',
        token: token,
        headerOnly: false,
      }
    );
  }

  saveLineGuideConfig(payload: LineGuideConfigSavePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/saveLineGuideConfig',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveLineGuideConfig',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadLineGuideImage(payload: {
    userId: number;
    menuId: number;
    base64: string;
    fileName?: string;
    fileSizeBytes?: number;
  }): Observable<{ isSuccess?: boolean; IsSuccess?: boolean; message?: string; Message?: string; imageUrl?: string; ImageUrl?: string }> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/uploadLineGuideImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadLineGuideImage',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getPublicGuidesForTable(params: {
    search?: string;
    isPublished?: boolean | null;
    isActive?: boolean | null;
    guideKind?: string | null;
    page?: number;
    pageSize?: number;
  }): Observable<PublicGuideTableResponse> {
    const token = this.cookieService.get('token');
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.search?.trim()) query['search'] = params.search.trim();
    if (params.isPublished === true || params.isPublished === false) {
      query['isPublished'] = params.isPublished;
    }
    if (params.isActive === true || params.isActive === false) {
      query['isActive'] = params.isActive;
    }
    if (params.guideKind === 'parking' || params.guideKind === 'ev') {
      query['guideKind'] = params.guideKind;
    }
    return this.apiMethodService.request<PublicGuideTableResponse>(
      '/SystemSettings/getPublicGuidesForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getPublicGuidesForTable',
        token: token,
        headerOnly: false,
        params: query,
      }
    );
  }

  getPublicGuideById(id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/getPublicGuideById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getPublicGuideById',
        token: token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  savePublicGuide(payload: PublicGuideSavePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/savePublicGuide',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'savePublicGuide',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadPublicGuideImage(payload: {
    userId: number;
    menuId: number;
    base64: string;
    fileName?: string;
    fileSizeBytes?: number;
  }): Observable<{
    isSuccess?: boolean;
    IsSuccess?: boolean;
    message?: string;
    Message?: string;
    imagePath?: string;
    ImagePath?: string;
    imageExt?: string;
    ImageExt?: string;
    imageUrl?: string;
    ImageUrl?: string;
  }> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/uploadPublicGuideImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadPublicGuideImage',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deletePublicGuide(payload: { userId: number; menuId: number; id: number }): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/deletePublicGuide',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deletePublicGuide',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getManualContact(): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/getManualContact',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getManualContact',
        token: token,
        headerOnly: false,
      }
    );
  }

  saveManualContact(payload: {
    userId: number;
    menuId: number;
    id: number;
    companyNameTh: string;
    addressTh?: string | null;
    phone?: string | null;
    phoneSecondary?: string | null;
    email?: string | null;
    noteTh?: string | null;
    isActive: boolean;
  }): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/saveManualContact',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveManualContact',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getSitePublicProfilesForTable(params: {
    search?: string;
    isPublished?: boolean | null;
    isActive?: boolean | null;
    siteIsOnline?: boolean | null;
    page?: number;
    pageSize?: number;
  }): Observable<SitePublicTableResponse> {
    const token = this.cookieService.get('token');
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.search?.trim()) query['search'] = params.search.trim();
    if (params.isPublished === true || params.isPublished === false) {
      query['isPublished'] = params.isPublished;
    }
    if (params.isActive === true || params.isActive === false) {
      query['isActive'] = params.isActive;
    }
    if (params.siteIsOnline === true || params.siteIsOnline === false) {
      query['siteIsOnline'] = params.siteIsOnline;
    }
    return this.apiMethodService.request<SitePublicTableResponse>(
      '/SystemSettings/getSitePublicProfilesForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getSitePublicProfilesForTable',
        token: token,
        headerOnly: false,
        params: query,
      }
    );
  }

  getSitePublicProfileById(id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/getSitePublicProfileById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getSitePublicProfileById',
        token: token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  getSitePublicProfileSiteOptions(search?: string): Observable<any[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string> = {};
    if (search?.trim()) params['search'] = search.trim();
    return this.apiMethodService.request(
      '/SystemSettings/getSitePublicProfileSiteOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getSitePublicProfileSiteOptions',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  saveSitePublicProfile(payload: SitePublicSavePayload): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/saveSitePublicProfile',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveSitePublicProfile',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadSitePublicImage(payload: {
    userId: number;
    menuId: number;
    base64: string;
    fileName?: string;
    fileSizeBytes?: number;
  }): Observable<{
    isSuccess?: boolean;
    IsSuccess?: boolean;
    message?: string;
    Message?: string;
    imagePath?: string;
    ImagePath?: string;
    imageExt?: string;
    ImageExt?: string;
    imageUrl?: string;
    ImageUrl?: string;
  }> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/uploadSitePublicImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadSitePublicImage',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deleteSitePublicProfile(payload: { userId: number; menuId: number; id: number }): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request(
      '/SystemSettings/deleteSitePublicProfile',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteSitePublicProfile',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getLineBroadcastsForTable(params: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Observable<LineBroadcastTableResponse> {
    const token = this.cookieService.get('token');
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.search?.trim()) query['search'] = params.search.trim();
    if (params.status) query['status'] = params.status;
    return this.apiMethodService.request<LineBroadcastTableResponse>(
      '/SystemSettings/getLineBroadcastsForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastsForTable',
        token: token,
        headerOnly: false,
        params: query,
      }
    );
  }

  getLineBroadcastById(id: number): Observable<LineBroadcastDetailDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastDetailDto>(
      '/SystemSettings/getLineBroadcastById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastById',
        token: token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  getLineBroadcastAudienceSummary(filter?: {
    siteIds?: number[];
    excludedUserIds?: number[];
  }): Observable<LineBroadcastAudienceSummaryDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string> = {};
    if (filter?.siteIds?.length) params['siteIds'] = filter.siteIds.join(',');
    if (filter?.excludedUserIds?.length) params['excludedUserIds'] = filter.excludedUserIds.join(',');
    return this.apiMethodService.request<LineBroadcastAudienceSummaryDto>(
      '/SystemSettings/getLineBroadcastAudienceSummary',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastAudienceSummary',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getLineBroadcastQuota(): Observable<LineBroadcastQuotaDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastQuotaDto>(
      '/SystemSettings/getLineBroadcastQuota',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastQuota',
        token: token,
        headerOnly: false,
      }
    );
  }

  saveLineBroadcast(payload: LineBroadcastSavePayload): Observable<LineBroadcastActionResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastActionResponse>(
      '/SystemSettings/saveLineBroadcast',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveLineBroadcast',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadLineBroadcastImage(payload: {
    userId: number;
    menuId: number;
    base64: string;
    fileName?: string;
    fileSizeBytes?: number;
  }): Observable<LineBroadcastImageUploadResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastImageUploadResponse>(
      '/SystemSettings/uploadLineBroadcastImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadLineBroadcastImage',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  testSendLineBroadcast(payload: LineBroadcastTestSendPayload): Observable<LineBroadcastActionResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastActionResponse>(
      '/SystemSettings/testSendLineBroadcast',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'testSendLineBroadcast',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  sendLineBroadcast(payload: LineBroadcastActionPayload): Observable<LineBroadcastActionResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastActionResponse>(
      '/SystemSettings/sendLineBroadcast',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'sendLineBroadcast',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  retryLineBroadcast(payload: LineBroadcastActionPayload): Observable<LineBroadcastActionResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastActionResponse>(
      '/SystemSettings/retryLineBroadcast',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'retryLineBroadcast',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deleteLineBroadcast(payload: LineBroadcastActionPayload): Observable<LineBroadcastActionResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastActionResponse>(
      '/SystemSettings/deleteLineBroadcast',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteLineBroadcast',
        token: token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getLineBroadcastProgress(id: number): Observable<LineBroadcastProgressDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastProgressDto>(
      '/SystemSettings/getLineBroadcastProgress',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastProgress',
        token: token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  getLineBroadcastSiteOptions(): Observable<LineBroadcastSiteOptionDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastSiteOptionDto[]>(
      '/SystemSettings/getLineBroadcastSiteOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastSiteOptions',
        token: token,
        headerOnly: false,
      }
    );
  }

  getLineBroadcastAudienceMembers(query: {
    audienceType: string;
    siteIds?: number[];
    search?: string;
    page?: number;
    pageSize?: number;
  }): Observable<LineBroadcastAudienceMembersResponse> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      audienceType: query.audienceType,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.siteIds?.length) params['siteIds'] = query.siteIds.join(',');
    if (query.search?.trim()) params['search'] = query.search.trim();
    return this.apiMethodService.request<LineBroadcastAudienceMembersResponse>(
      '/SystemSettings/getLineBroadcastAudienceMembers',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastAudienceMembers',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getLineBroadcastNewsOptions(search?: string): Observable<LineBroadcastNewsOptionDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string> = {};
    if (search?.trim()) params['search'] = search.trim();
    return this.apiMethodService.request<LineBroadcastNewsOptionDto[]>(
      '/SystemSettings/getLineBroadcastNewsOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastNewsOptions',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getLineBroadcastLinkSettings(): Observable<LineBroadcastLinkSettingsDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LineBroadcastLinkSettingsDto>(
      '/SystemSettings/getLineBroadcastLinkSettings',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastLinkSettings',
        token: token,
        headerOnly: false,
      }
    );
  }

  getClientFreeQuotasForTable(query: {
    siteId: number;
    search?: string;
    vehicleTypeId?: number | null;
    managedBy?: string | null;
    page?: number;
    pageSize?: number;
  }): Observable<ClientFreeQuotaTableResponse> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.search?.trim()) params['search'] = query.search.trim();
    if (query.vehicleTypeId && query.vehicleTypeId > 0) params['vehicleTypeId'] = query.vehicleTypeId;
    if (query.managedBy) params['managedBy'] = query.managedBy;
    return this.apiMethodService.request<ClientFreeQuotaTableResponse>(
      '/SystemSettings/getClientFreeQuotasForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getClientFreeQuotasForTable',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getFreeQuotaClientOptions(siteId: number, search?: string): Observable<FreeQuotaClientOptionDto[]> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId };
    if (search?.trim()) params['search'] = search.trim();
    return this.apiMethodService.request<FreeQuotaClientOptionDto[]>(
      '/SystemSettings/getFreeQuotaClientOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getFreeQuotaClientOptions',
        token: token,
        headerOnly: false,
        params,
      }
    );
  }

  getAccessGroupUserOptions(query: {
    siteId: number;
    search?: string;
    hideAssigned?: boolean;
    currentGroupId?: number | null;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = {
      siteId: query.siteId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.search?.trim()) params['search'] = query.search.trim();
    if (query.hideAssigned) params['hideAssigned'] = true;
    if (query.currentGroupId && query.currentGroupId > 0) params['currentGroupId'] = query.currentGroupId;
    return this.apiMethodService.request<any>(
      '/SystemSettings/getAccessGroupUserOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessGroupUserOptions',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getAccessGroupClientOptions(query: {
    siteId: number;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.search?.trim()) params['search'] = query.search.trim();
    return this.apiMethodService.request<any>(
      '/SystemSettings/getAccessGroupClientOptions',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessGroupClientOptions',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getAccessGroupMembers(groupId: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/SystemSettings/getAccessGroupMembers',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getAccessGroupMembers',
        token,
        headerOnly: false,
        params: { groupId },
      }
    );
  }

  getLineBroadcastMemberCards(query: { cpUserId: number; siteIds?: number[] }): Observable<LineBroadcastMemberCardsResponse> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { cpUserId: query.cpUserId };
    if (query.siteIds?.length) params['siteIds'] = query.siteIds.join(',');
    return this.apiMethodService.request<LineBroadcastMemberCardsResponse>(
      '/SystemSettings/getLineBroadcastMemberCards',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getLineBroadcastMemberCards',
        token,
        headerOnly: false,
        params,
      }
    );
  }
}
